import { useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import BugReportIcon from "@mui/icons-material/BugReport";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import RouteIcon from "@mui/icons-material/Route";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { LoadingCenter } from "@components/LoadingCenter";

export default function Home() {
  const { t } = useTranslation();
  const { isAuthenticated, loading, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return <LoadingCenter minHeight="70vh" />;
  }

  const features = [
    {
      icon: <RuleFolderIcon sx={{ color: "primary.main" }} />,
      title: t("home.feature1Title"),
      desc: t("home.feature1Desc"),
    },
    {
      icon: <RouteIcon sx={{ color: "secondary.main" }} />,
      title: t("home.feature2Title"),
      desc: t("home.feature2Desc"),
    },
    {
      icon: <AutoGraphIcon sx={{ color: "success.main" }} />,
      title: t("home.feature3Title"),
      desc: t("home.feature3Desc"),
    },
    {
      icon: <TaskAltIcon sx={{ color: "warning.main" }} />,
      title: t("home.feature4Title"),
      desc: t("home.feature4Desc"),
    },
  ];

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 3, md: 6 }}
          sx={{
            alignItems: "center",
          }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Chip label={t("app.subtitle")} color="primary" sx={{ mb: 2 }} />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2rem", md: "3rem" },
                lineHeight: 1.15,
                mb: 2,
              }}
            >
              {t("home.hero")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: 560,
                mb: 3,
                lineHeight: 1.75,
              }}
            >
              {t("home.heroSubtitle")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                onClick={loginWithGoogle}
              >
                {t("account.loginWithGoogle")}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/preview"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: 16,
                  textAlign: "center",
                  justifyContent: "center",
                }}
              >
                {t("preview.cta")}
              </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ maxWidth: 500, mx: "auto" }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <BugReportIcon sx={{ fontSize: 34, color: "primary.main" }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {t("home.cardTitle")}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: "text.secondary",
                    mb: 2.5,
                  }}
                >
                  {t("home.cardSubtitle")}
                </Typography>
                <Divider sx={{ mb: 2.5 }} />
                <Stack spacing={1.2}>
                  <Typography variant="body2">
                    - {t("home.cardPoint1")}
                  </Typography>
                  <Typography variant="body2">
                    - {t("home.cardPoint2")}
                  </Typography>
                  <Typography variant="body2">
                    - {t("home.cardPoint3")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 5, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              textAlign: "center",
            }}
          >
            {t("home.featuresTitle")}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              textAlign: "center",
              mb: 4,
            }}
          >
            {t("home.featuresSubtitle")}
          </Typography>
          <Grid container spacing={2.5}>
            {features.map((feature) => (
              <Grid size={{ xs: 12, sm: 6 }} key={feature.title}>
                <Card sx={{ height: "100%" }} variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
